import { Area } from 'react-easy-crop';
import * as Util from 'common/util.bs';

type imageConfig = {
  imageSrc: string;
  pixelCrop: Area;
  resizeParams?: resizeParams;
  resize?: boolean;
};

type resizeParams = {
  width: number;
  height: number;
};

const useCustomCardDesign = () => {
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
      image.src = url;
    });

  const generateImageBlob = async ({
    imageSrc,
    pixelCrop,
    resizeParams = {
      width: 452,
      height: 280,
    },
    resize = true,
  }: imageConfig): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');

    if (!canvasContext) {
      return null;
    }

    canvas.width = image.width;
    canvas.height = image.height;
    canvasContext.drawImage(image, 0, 0);

    const data = canvasContext.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    canvasContext.putImageData(data, 0, 0);

    if (resize) {
      const resizedCanvas = document.createElement('canvas');
      const ctx = resizedCanvas.getContext('2d');

      resizedCanvas.width = resizeParams.width;
      resizedCanvas.height = resizeParams.height;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, resizeParams.width, resizeParams.height);
      return new Promise(resolve => {
        resizedCanvas.toBlob(file => {
          resolve(file);
        }, 'image/jpeg');
      });
    } else {
      return new Promise(resolve => {
        canvas.toBlob(file => {
          resolve(file);
        }, 'image/jpeg');
      });
    }
  };

  const getImageFileDataUrl = (imageFile?: File) => {
    const encodeImageFileAsURL = () => {
      if (!imageFile || !imageFile.type.startsWith('image/')) return '';
      const image = new Image();
      image.src = URL.createObjectURL(imageFile);
      return image.src;
    };

    return { encodeImageFileAsURL };
  };

  const uploadImage = (blob: Blob, templateUid: string, endpointUrl: string) => {
    const headers = Util.HeadersUtil.makeWithArray(
      Util.HeadersUtil.withAuthHeaderIfAvailable(
        Util.HeadersUtil.acceptContentTypeJson(Util.HeadersUtil.build(undefined))
      )
    );

    const formData = new FormData();
    formData.set('uid', templateUid);
    formData.append('image', blob, 'image.jpg');

    const init = {
      headers,
      method: 'POST',
      body: formData,
    };
    return fetch(endpointUrl, init);
  };

  return {
    createImage,
    generateImageBlob,
    getImageFileDataUrl,
    uploadImage,
  };
};

export default useCustomCardDesign;
